<script setup>
import { computed, nextTick, ref } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@axios'
import { useReservationsStore, useRestaurantsStore, useUserGroupsStore, useUsersStore } from '@/stores'
import { useAgentChat } from '@/composables/useAgentChat'
import { actorByline } from '@/utils/textingActor'
import { formatMarkdown as formatAgentMarkdown, isDiscoveryPageText, splitDiscoveryBlocks, takeRowLines } from '@/utils/agentMessageHtml'
import {
  AVAILABILITY_ROW_TOKEN_RE,
  PARTIAL_AVAILABILITY_ROW_RE,
  availabilityRowItem,
  availabilityRowPlainText,
  decodeAvailabilityRow,
} from '@/utils/availabilityRow'
import {
  DISCOVERY_ROW_TOKEN_RE,
  PARTIAL_DISCOVERY_ROW_RE,
  decodeDiscoveryRow,
  discoveryRowPlainText,
} from '@/utils/discoveryRow'
import { mediaUrl } from '@core/utils/mediaUrl'
import MemberProfileCard from '@core/components/MemberProfileCard.vue'
import AgentSendToContact from '@core/components/AgentSendToContact.vue'
import AgentNoteChoice from '@core/components/AgentNoteChoice.vue'
import RestaurantGridCard from '@core/components/home/content/RestaurantGridCard.vue'
import AgentRestaurantRow from '@core/components/AgentRestaurantRow.vue'
import AgentAiPlaceRow from '@core/components/AgentAiPlaceRow.vue'
import AgentRequestLikelihood from '@core/components/AgentRequestLikelihood.vue'
import AskRmFeedback from '@core/components/AskRmFeedback.vue'
import { SPEND_GUIDANCE_QUESTION, askRmReservationId } from '@/utils/spendGuidance'

const props = defineProps({
  role: {
    type: String,
    required: true,
    validator: v => ['user', 'assistant', 'error'].includes(v),
  },
  content: {
    type: String,
    default: '',
  },
  isAdmin: {
    type: Boolean,
    default: false,
  },
  msgDbIndex: {
    type: Number,
    default: null,
  },
  // RM-262 — choice chips stay live ONLY on the newest message, and never while
  // a turn is generating. They used to render tappable forever on every message
  // that carried the token: a member who tapped "I know where" and got a reply
  // that didn't obviously acknowledge the tap would tap it again, send the same
  // text, and get the same line back (prod 2026-08-24, Bruce Lewitas — he left
  // without ever naming a restaurant; Kapil reproduced it). Defaults true so
  // any other caller keeps today's behavior rather than silently losing chips.
  // The chip the member tapped on THIS reply (from the following chip message):
  // rendered filled, so the choice reads in place of an echoed bubble.
  selectedOption: {
    type: String,
    default: null,
  },
  chipsActive: {
    type: Boolean,
    default: true,
  },
  hasTrace: {
    type: Boolean,
    default: false,
  },
  // RM-208: annotation state for admin review of AI replies.
  annotated: {
    type: Boolean,
    default: false,
  },
  annotationStatus: {
    type: String,
    default: null,
  },
  // RM-208: turn latency summary ({ latencyMs, toolsUsed }) — admin viewer only.
  turnTrace: {
    type: Object,
    default: null,
  },
  // Enables member 👍/👎 on suggestion replies (posts against this conversation).
  conversationId: {
    type: String,
    default: null,
  },
  memberRating: {
    type: String,
    default: null,
  },
  timestamp: {
    type: String,
    default: '',
  },
  bubbleTime: {
    type: String,
    default: '',
  },
  // RM-205: which channel the message arrived/was sent on ('web' | 'email' | 'sms').
  channel: {
    type: String,
    default: 'web',
  },
  // RM-399 (10/21, admin only): the text parts a texted reply actually went out as
  // ({ channel, parts, messageKey, at }), null for a reply stored before they were recorded.
  sent: {
    type: Object,
    default: null,
  },
  // The admin viewer's Text view: show `sent.parts` verbatim instead of the app rendering.
  textView: {
    type: Boolean,
    default: false,
  },
  // RM-380: card data for a conversation that isn't the viewer's own (RM Team's conversations
  // page): { memberProfiles: { [userId]: profile }, restaurantCards: { [id]: card } }.
  // Absent = the viewer's own chat, whose cards live in useAgentChat.
  cardData: {
    type: Object,
    default: null,
  },
  // RM-353: someone texting FOR the member ({ name, email, onBehalfOf }). Shown in the
  // admin view and, since D7 (9/30), in the member's own chat: the member sees who acted.
  actor: {
    type: Object,
    default: null,
  },
  // RM-205 member support: a person at RM wrote this reply, not RM AI.
  supportReply: {
    type: Boolean,
    default: false,
  },
  // RM-398: a group text thread, read in the app. `senderName` is who wrote the
  // message when it wasn't the viewer; `fromOther` puts it on the left, under
  // that name, so the viewer's own messages stay the only ones on the right.
  senderName: {
    type: String,
    default: '',
  },
  fromOther: {
    type: Boolean,
    default: false,
  },
  // RM-212: reply the member cut off mid-generation (renders a "stopped" caption).
  stopped: {
    type: Boolean,
    default: false,
  },
  // RM-212: repeated member message from a regenerate — renders as a divider,
  // not a duplicate bubble.
  regenerated: {
    type: Boolean,
    default: false,
  },
  // RM-212: parent enables the regenerate action on the last AI reply only.
  showRegenerate: {
    type: Boolean,
    default: false,
  },
  // File the member attached to this message (receipt, profile photo) —
  // an image renders as a thumbnail, a PDF as a document chip.
  attachedImageId: {
    type: String,
    default: null,
  },
  attachedFileType: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['showTrace', 'annotate', 'selectOption', 'openReservationRequest', 'regenerate', 'openSupport', 'openHelp'])

/**
 * An attached image opens full-size in place (Kapil, 2026-09-23: "I can use the
 * magnifying glass to zoom in too"). A business card in the bubble is ~90px
 * tall, where no rendition is sharp enough to read 8pt type — the fix is a
 * bigger look at it, not a bigger file.
 */
const zoomOpen = ref(false)
// RM-359 (Kapil 10/30): "ask RM" in RM AI's spend message opens Ask RM for feedback on that request
const askRmId = ref(null)
const askRmDialog = ref(null)

const router = useRouter()
const reservationsStore = useReservationsStore()
const restaurantsStore = useRestaurantsStore()
const userGroupsStore = useUserGroupsStore()
const usersStore = useUsersStore()
const agentChat = useAgentChat()

// RM-380: the conversations page shows another member's conversation; reading the viewer's own
// chat cards there left every profile and restaurant card spinning.
const memberProfiles = computed(() => props.cardData?.memberProfiles || agentChat.memberProfiles.value)
const restaurantCards = computed(() => props.cardData?.restaurantCards || agentChat.restaurantCards.value)

async function navigateToBookings(location = { path: '/bookings' }) {
  if (router.currentRoute.value.fullPath === '/bookings' && location.path === '/bookings' && !location.query) {
    return
  }

  await router.push(location)
}

async function startPrivateDiningInquiry() {
  await usersStore.showLoadingOverlay()
  try {
    const isPrimaryAdmin = usersStore.myUser?.primaryGroupType !== 'restaurant'
      && (
        usersStore.myUser?.primaryGroupPermission === 'admin'
        || usersStore.myUser?.primaryGroupPermission === 'adminStandard'
      )

    if (isPrimaryAdmin) {
      await userGroupsStore.getAllUserGroups()
      await navigateToBookings({ path: '/bookings', query: { startPrivateDining: '1' } })

      return
    }

    if (!usersStore.myUser) {
      await usersStore.loginNewDummyUser()
    }

    await usersStore.startNewReservation({ reservationType: 'private' })
    await nextTick()
    await navigateToBookings()
  } finally {
    await usersStore.hideLoadingOverlay()
  }
}

async function openRestaurantDetails(restaurantRef) {
  const profileId = typeof restaurantRef === 'string' ? restaurantRef : restaurantRef?.profileId
  const restaurantId = typeof restaurantRef === 'object' ? restaurantRef?._id : null
  let restaurant = null

  if (profileId) {
    restaurant = await restaurantsStore.getRestaurantByProfileId(profileId)
  } else if (restaurantId) {
    restaurant = await restaurantsStore.getRestaurantById(restaurantId)
  }

  if (restaurant) {
    restaurantsStore.viewingDetailsRestaurantTab = 'Details'
    restaurantsStore.viewingDetailsRestaurant = restaurant
  }
}

async function handleContentClick(e) {
  const addFav = e.target.closest('button.agent-add-favorite')
  if (addFav) {
    e.preventDefault()
    const name = addFav.dataset.name || ''
    const city = addFav.dataset.city || ''
    if (name) emit('selectOption', `Add ${name}${city ? ` in ${city}` : ''} to my favorites`)

    return
  }
  const link = e.target.closest('a.agent-restaurant-link') || e.target.closest('a.agent-info-link') || e.target.closest('a.agent-internal-link') || e.target.closest('a.agent-submit-cta')
  if (!link) return

  e.preventDefault()
  const href = link.getAttribute('href')

  // Review & Submit Request CTA — open the review dialog in place (over the
  // AI screen). Parent handles opening; closing returns to the chat.
  const submitMatch = href.match(/^\/submit-request\/(.+)$/)
  if (submitMatch) {
    emit('openReservationRequest', submitMatch[1])

    return
  }

  // "ask RM" in RM AI's spend message (RM-359, Kapil 10/30): Ask RM for feedback on this
  // request, in place, with the question already in it - the same flow the request screens use.
  const askRmFor = askRmReservationId(href)
  if (askRmFor) {
    askRmId.value = askRmFor
    await nextTick()
    askRmDialog.value?.open(SPEND_GUIDANCE_QUESTION)

    return
  }

  // Info dialog links (e.g., /info/how-rm-works, /info/faqs)
  const infoMatch = href.match(/^\/info\/(.+)$/)
  if (infoMatch) {
    usersStore.membersInfoDialogTab = infoMatch[1]
    usersStore.isShowingMembersInfoDialog = true

    return
  }

  // Restaurant detail links (e.g., /r/carbone)
  if (href.startsWith('/r/')) {
    const profileId = href.replace(/^\/r\//, '')
    if (profileId) {
      await openRestaurantDetails(profileId)
    }

    return
  }

  // Support link — open the in-place support dialog over the chat (RM-212;
  // previously navigated to /messages and lost the AI conversation). The
  // admin conversation viewer doesn't handle the event, so it's a no-op there.
  if (href === '/support') {
    emit('openSupport')

    return
  }

  if (href === '/bookings?startPrivateDining=1') {
    await startPrivateDiningInquiry()

    return
  }

  // General internal links — navigate via Vue Router
  await router.push(href)
}

const showTraceIcon = computed(() => props.isAdmin && props.role === 'user' && props.msgDbIndex != null)

// RM-205: badge messages that traveled over an external channel
const channelLabel = computed(() => {
  if (props.supportReply) return 'RM member support'
  const via = ({ email: 'via email', sms: 'via text' })[props.channel] || ''
  const byline = actorByline(props.actor)
  
  return [via, byline].filter(Boolean).join(' · ')
})

// RM-208: admins can annotate any AI reply that has a persisted db index.
const showAnnotateIcon = computed(() => props.isAdmin && props.role === 'assistant' && props.msgDbIndex != null)

// RM-399 (10/21): in the admin viewer's Text view a texted reply shows the parts as sent;
// a texted reply stored before the parts were recorded keeps the app rendering, captioned.
const sentParts = computed(() => (props.sent && Array.isArray(props.sent.parts) ? props.sent.parts.filter(p => typeof p === 'string' && p.length) : []))
const showSentText = computed(() => props.isAdmin && props.textView && props.role === 'assistant' && sentParts.value.length > 0)
const sentTextMissing = computed(() => props.isAdmin && props.textView && props.role === 'assistant' && props.channel === 'sms' && sentParts.value.length === 0)

// Plain-English label for the annotation's tracking status.
const ANNOTATION_STATUS_LABELS = {
  open: 'Needs to be fixed',
  in_progress: 'In progress',
  ready_for_verification: 'In progress',
  verified: 'Complete',
  wont_fix: "Won't fix",
  duplicate: 'Duplicate',
}
const annotationStatusLabel = computed(() => ANNOTATION_STATUS_LABELS[props.annotationStatus] || 'Needs to be fixed')

// ─── Reply Actions row ────────────────────────────────────────────────────
// Member 👍/👎, copy, and message-support under EVERY assistant reply
// (Kapil 2026-07-18 — originally only suggestion/card/hand-off replies, but
// that hid feedback exactly where the annotation loop most needs it: wrong
// dates, bad no-matches replies, clumsy clarifying questions). Regenerate
// keeps its own last-reply-only gate.
const localRating = ref(undefined)
const effectiveRating = computed(() => (
  localRating.value !== undefined ? localRating.value : props.memberRating
))
const copied = ref(false)
const ratingSaving = ref(false)

// Shown in the LIVE chat only — the admin conversation viewer never passes
// conversationId, so these can't appear there. (Admin users chatting as
// members see them too; isAdmin here means "admin user", not "admin viewer".)
// The bar exists when there is anything in it.
const showMemberActions = computed(() =>
  props.role === 'assistant'
  && props.msgDbIndex != null
  && !!props.conversationId,
)

// The bar exists when there is anything in it. `showRegenerate` is a prop, not
// a ref — it has no .value.
const showActionBar = computed(() => showMemberActions.value || props.showRegenerate)

async function rateReply(rating) {
  if (ratingSaving.value) return

  // Tapping the thumb you already chose undoes it. Without this a member who
  // mis-tapped was stuck with the rating forever, and a stray thumbs-down sat
  // in the annotation queue as a real complaint.
  const next = effectiveRating.value === rating ? null : rating

  ratingSaving.value = true
  try {
    await axios.post(`/agent/conversations/${props.conversationId}/member-feedback`, {
      messageIndex: props.msgDbIndex,
      rating: next,
    }, {
      params: { jwt: JSON.parse(localStorage.getItem('jwt')) },
    })
    localRating.value = next
  } catch (err) {
    console.error('rateReply failed', err)
  } finally {
    ratingSaving.value = false
  }
}

async function copyReplyText() {
  // Copy what the member actually reads: restaurant cards become text lines
  // ("Preferred · Name — Group"), markdown links keep their label, other
  // display tokens and the opt-in AI-suggestions question are dropped.
  const plain = props.content
    // An availability row: its name, times and booking link, as the member reads them.
    .replace(AVAILABILITY_ROW_TOKEN_RE, (_, payload) => availabilityRowPlainText(decodeAvailabilityRow(payload)))
    // A Discovery row done by text: its name and what RM knows under it, as the card shows them.
    .replace(DISCOVERY_ROW_TOKEN_RE, (_, payload) => discoveryRowPlainText(decodeDiscoveryRow(payload)))
    // A row's own line ("Booked 10+ times, incl last 3mo") is copied with it, on the next line.
    .replace(/\{\{restaurant-(?:card|option):([^}]+)\}\}(?:\n\{\{small:([^}]+)\}\})?/g, (_, id, line) => {
      const r = restaurantCards.value[id.trim()]
      if (!r) return ''
      const name = r.name ?? r.raw?.name ?? ''
      const group = r.group ?? r.raw?.group
      const preferred = (r.status ?? r.raw?.status) === 'Preferred'

      return `${preferred ? 'Preferred · ' : ''}${name}${group ? ` — ${group}` : ''}${line ? `\n${line.trim()}` : ''}`
    })
    .replace(/\{\{[^}]+\}\}\n?/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .split('\n')
    .filter(line => !/additional suggestions via AI/i.test(line))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
  try {
    await navigator.clipboard.writeText(plain)

    // Stays filled once copied (no reset): the icon doubles as a record of
    // which replies the member already took with them.
    copied.value = true
  } catch (err) {
    console.error('copy failed', err)
  }
}

function messageSupport() {
  // RM-212: open the support thread in a dialog over the chat instead of
  // navigating away to /messages.
  emit('openSupport')
}

// RM-208: "6.2s · 4 tools · 4:24 PM" caption under AI replies in the admin viewer.
const turnTraceCaption = computed(() => {
  if (!props.isAdmin || props.role !== 'assistant') return ''
  const parts = []
  if (props.turnTrace?.latencyMs != null) parts.push(`${(props.turnTrace.latencyMs / 1000).toFixed(1)}s`)
  if (props.turnTrace?.toolsUsed) parts.push(`${props.turnTrace.toolsUsed} tool${props.turnTrace.toolsUsed > 1 ? 's' : ''}`)
  if (props.timestamp) {
    parts.push(new Date(props.timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }))
  }

  return parts.join(' · ')
})

const preferredIconSvg = `<svg width="14" height="18" viewBox="0 0 10 13" fill="none" xmlns="http://www.w3.org/2000/svg" style="display:inline;vertical-align:middle;margin-right:3px;position:relative;top:-1px"><path d="M6.87435 3.65293C6.98067 3.54826 7.14015 3.54826 7.24647 3.65293C7.35279 3.75759 7.35279 3.9146 7.24647 4.01926L5.06689 6.16496C5.01374 6.21729 4.96057 6.24346 4.88083 6.24346C4.80109 6.24346 4.74793 6.21729 4.69477 6.16496L3.68472 5.17061C3.5784 5.06595 3.5784 4.90894 3.68472 4.80428C3.79104 4.69961 3.95053 4.69961 4.05684 4.80428L4.88083 5.61545L6.87435 3.65293Z" fill="#3F998F"/><path fill-rule="evenodd" clip-rule="evenodd" d="M9.25109 4.95091C9.20325 4.87929 9.17767 4.79479 9.17767 4.70831C9.17767 4.62182 9.20325 4.53732 9.25109 4.46571L9.54206 4.03635C9.61502 3.9287 9.66256 3.80551 9.68102 3.67628C9.69949 3.54705 9.68838 3.41525 9.64855 3.29107C9.60872 3.16689 9.54124 3.05365 9.45133 2.9601C9.36142 2.86657 9.25149 2.79523 9.13003 2.75162L8.64577 2.57734C8.56552 2.54808 8.49581 2.49505 8.4457 2.42511C8.39559 2.35517 8.36741 2.27158 8.36482 2.18519L8.35063 1.66487C8.34713 1.5344 8.31409 1.40648 8.25406 1.291C8.19404 1.17551 8.10863 1.07554 8.00442 0.998816C7.90022 0.922088 7.78001 0.870645 7.65308 0.848455C7.52614 0.826277 7.39588 0.833933 7.27233 0.870866L6.77972 1.01807C6.69781 1.0424 6.61057 1.04114 6.52938 1.01442C6.4482 0.987703 6.37686 0.936784 6.32469 0.868329L6.0116 0.455459C5.92949 0.357472 5.82733 0.278754 5.71222 0.224763C5.59709 0.170784 5.47176 0.142822 5.34491 0.142822C5.21806 0.142822 5.09272 0.170784 4.9776 0.224763C4.86248 0.278754 4.76033 0.357472 4.67823 0.455459L4.36513 0.868329C4.31277 0.936551 4.24142 0.987296 4.1603 1.014C4.07918 1.04069 3.99202 1.04211 3.91009 1.01807L3.41749 0.870866C3.29397 0.833945 3.16375 0.826277 3.03684 0.848432C2.90994 0.870598 2.78975 0.922006 2.68555 0.998688C2.58136 1.07537 2.49594 1.17528 2.43589 1.29072C2.37583 1.40615 2.34275 1.53402 2.33919 1.66446L2.32499 2.18604C2.32232 2.27236 2.2941 2.35586 2.244 2.42571C2.1939 2.49556 2.12424 2.54854 2.04404 2.57776L1.55979 2.75204C1.43833 2.79565 1.3284 2.86699 1.23848 2.96054C1.14857 3.05407 1.0811 3.16731 1.04127 3.29149C1.00144 3.41568 0.99033 3.54748 1.00879 3.6767C1.02726 3.80593 1.0748 3.92913 1.14775 4.03676L1.43872 4.46613C1.48648 4.53771 1.512 4.62213 1.512 4.70852C1.512 4.79492 1.48648 4.87934 1.43872 4.95091L1.14775 5.3807C1.07488 5.4883 1.02741 5.61143 1.00899 5.74058C0.990566 5.86973 1.0017 6.00145 1.04152 6.12554C1.08135 6.24964 1.1488 6.3628 1.23867 6.45626C1.32853 6.54973 1.4384 6.621 1.55979 6.66458L2.04404 6.83929C2.12471 6.86788 2.19484 6.92074 2.24506 6.99083C2.29526 7.06091 2.32315 7.14485 2.32499 7.23143L2.33919 7.75176C2.34261 7.88225 2.37559 8.01022 2.4356 8.12575C2.4956 8.24128 2.58101 8.34128 2.68524 8.41802C2.78946 8.49477 2.9097 8.54621 3.03667 8.56836C3.10165 8.57969 3.16749 8.58321 3.23286 8.57897V12.2197C3.23283 12.2534 3.24156 12.2866 3.25814 12.3157C3.27472 12.3449 3.29856 12.3691 3.32723 12.3857C3.3559 12.4023 3.38837 12.4109 3.42131 12.4104C3.45426 12.41 3.4865 12.4006 3.51475 12.3832L5.4656 11.1074L7.41645 12.3832C7.44469 12.4006 7.47694 12.41 7.50988 12.4104C7.54283 12.4109 7.57529 12.4023 7.60396 12.3857C7.63263 12.3691 7.65648 12.3449 7.67306 12.3157C7.68964 12.2866 7.69836 12.2534 7.69833 12.2197V8.55896C7.80848 8.53334 7.91257 8.48543 8.00426 8.41794C8.10846 8.34126 8.19387 8.24134 8.25393 8.12591C8.31398 8.01048 8.34707 7.88261 8.35063 7.75217L8.36482 7.23143C8.36666 7.14485 8.39455 7.06091 8.44476 6.99083C8.49497 6.92074 8.5651 6.86788 8.64577 6.83929L9.13003 6.66458C9.25142 6.621 9.36128 6.54973 9.45115 6.45626C9.54101 6.3628 9.60847 6.24964 9.64829 6.12554C9.68812 6.00145 9.69925 5.86973 9.68083 5.74058C9.66241 5.61143 9.61494 5.4883 9.54206 5.3807L9.25109 4.95091ZM3.60498 11.7434V8.48974L3.91009 8.39856C3.99202 8.37446 4.07919 8.37584 4.16033 8.40255C4.24146 8.42924 4.31281 8.48002 4.36513 8.5483L4.67823 8.96117C4.75679 9.06465 4.85776 9.14845 4.97336 9.20612C5.08895 9.26379 5.21607 9.29377 5.34491 9.29377C5.47375 9.29377 5.60087 9.26379 5.71646 9.20612C5.83205 9.14845 5.93303 9.06465 6.0116 8.96117L6.32469 8.5483C6.37719 8.48025 6.44855 8.42965 6.52962 8.40297C6.6107 8.37628 6.69778 8.37475 6.77972 8.39856L7.27233 8.54576C7.29016 8.55109 7.30813 8.55582 7.32621 8.55993V11.7434C7.32621 11.8037 7.2611 11.8401 7.21157 11.8075L5.56142 10.7216C5.53248 10.7037 5.49936 10.6943 5.4656 10.6943C5.43183 10.6943 5.39871 10.7037 5.36977 10.7216L3.71963 11.8075C3.67009 11.8401 3.60498 11.8037 3.60498 11.7434ZM9.25109 5.99409C9.23091 6.05645 9.19696 6.11333 9.15182 6.16039C9.10668 6.20746 9.05154 6.24346 8.9906 6.26567L8.50634 6.43996C8.34674 6.4981 8.20809 6.60353 8.1084 6.74257C8.00872 6.8816 7.9526 7.04781 7.94736 7.21959L7.93317 7.73991C7.93148 7.80561 7.9149 7.87002 7.88471 7.92819C7.85452 7.98636 7.81153 8.03671 7.75907 8.07535C7.70661 8.114 7.64608 8.1399 7.58217 8.15105C7.51825 8.1622 7.45266 8.15831 7.39047 8.13967L6.89786 7.99245C6.73504 7.94466 6.56181 7.94749 6.4006 8.00058C6.23939 8.05368 6.09762 8.1546 5.99364 8.29026L5.68055 8.7027C5.6392 8.75201 5.58777 8.79163 5.52981 8.81879C5.47185 8.84596 5.40876 8.86003 5.34491 8.86003C5.28105 8.86003 5.21796 8.84596 5.16001 8.81879C5.10205 8.79163 5.05062 8.75201 5.00927 8.7027L4.69617 8.29026C4.6171 8.18686 4.51572 8.10318 4.3998 8.04561C4.28388 7.98806 4.15649 7.95814 4.0274 7.95819C3.94772 7.95797 3.86843 7.96936 3.79195 7.99203L3.29935 8.14008C3.23712 8.15874 3.1715 8.16262 3.10755 8.15145C3.0436 8.14028 2.98305 8.11435 2.93058 8.07566C2.87811 8.03697 2.83513 7.98656 2.80497 7.92833C2.77481 7.87012 2.75827 7.80564 2.75665 7.73991L2.74246 7.21959C2.73718 7.04776 2.68101 6.88152 2.58124 6.74248C2.48148 6.60343 2.34274 6.49803 2.18306 6.43996L1.6988 6.26567C1.63781 6.24357 1.58264 6.20758 1.53754 6.16048C1.49243 6.11338 1.4586 6.05641 1.43864 5.99397C1.41868 5.93153 1.41312 5.86529 1.4224 5.80032C1.43168 5.73537 1.45554 5.67343 1.49216 5.61929L1.78271 5.18993C1.87799 5.0478 1.92894 4.87992 1.92894 4.70811C1.92894 4.53628 1.87799 4.36841 1.78271 4.22628L1.49174 3.79691C1.45514 3.74271 1.43131 3.68072 1.42209 3.61573C1.41287 3.55073 1.41849 3.48445 1.43854 3.422C1.45858 3.35955 1.49251 3.30259 1.53771 3.25553C1.5829 3.20846 1.63816 3.17254 1.69922 3.15053L2.18348 2.97624C2.34308 2.91811 2.48173 2.81268 2.58141 2.67364C2.6811 2.5346 2.73721 2.36839 2.74246 2.19662L2.75665 1.6763C2.7585 1.61063 2.77514 1.54627 2.80531 1.48812C2.83549 1.42998 2.87838 1.37959 2.93073 1.34085C2.98343 1.30268 3.04395 1.27708 3.10778 1.26594C3.17161 1.25481 3.2371 1.25844 3.29935 1.27654L3.79195 1.42375C3.95471 1.47201 4.12804 1.46945 4.28934 1.4164C4.45063 1.36335 4.59241 1.26228 4.69617 1.12637L5.00927 0.7135C5.05062 0.664187 5.10205 0.624578 5.16001 0.597407C5.21796 0.570237 5.28105 0.556169 5.34491 0.556169C5.40876 0.556169 5.47185 0.570237 5.52981 0.597407C5.58777 0.624578 5.6392 0.664187 5.68055 0.7135L5.99364 1.12637C6.09756 1.26209 6.23935 1.36302 6.40059 1.41606C6.56184 1.4691 6.73509 1.47177 6.89786 1.42375L7.39047 1.27654C7.45263 1.2579 7.51818 1.25401 7.58207 1.26513C7.64595 1.27625 7.70647 1.30212 7.75891 1.34072C7.81137 1.37931 7.85436 1.42961 7.88458 1.48773C7.91479 1.54584 7.93142 1.61021 7.93317 1.67587L7.94736 2.19619C7.95256 2.3681 8.00869 2.53442 8.10846 2.67356C8.20823 2.81268 8.34702 2.91816 8.50676 2.97624L8.99102 3.15053C9.052 3.17264 9.10717 3.20862 9.15228 3.25573C9.19738 3.30283 9.23122 3.35979 9.25118 3.42223C9.27114 3.48467 9.2767 3.55092 9.26742 3.61588C9.25814 3.68084 9.23427 3.74277 9.19766 3.79691L8.9071 4.22628C8.81182 4.36841 8.76088 4.53628 8.76088 4.70811C8.76088 4.87992 8.81182 5.0478 8.9071 5.18993L9.19766 5.61929C9.23416 5.67351 9.25795 5.73546 9.26721 5.80042C9.27647 5.86538 9.27096 5.93162 9.25109 5.99409Z" fill="#3F998F"/></svg>`

const spendOptions = computed(() => {
  if (!props.content) return []
  const match = props.content.match(/\{\{spend-options:([^}]+)\}\}/)
  if (!match) return []

  return match[1].match(/\$\d{1,3}(?:,\d{3})*/g) || []
})

/**
 * {{share-options:Network|Sunda - Fulton Market|Both|Keep private}} — RM-294.
 *
 * ONE sharing decision, asked once. Pipe-separated rather than comma, because
 * a restaurant name can contain a comma and the label IS the restaurant's own
 * name — the member is never asked to understand "Restaurant sharing" as an RM
 * concept. The server decides which options exist, so an option the server
 * would refuse is never rendered.
 */
const shareOptions = computed(() => {
  if (!props.content) return []
  // {{note-options:Add to 1|Add to 2|Save as a new note}} — "Add this to one
  // of them, or save it as a new note?" (2026-09-23): the same one-decision
  // chips, and a tap is the member's answer.
  const match = props.content.match(/\{\{(?:share|note)-options:([^}]+)\}\}/)
  if (!match) return []

  return match[1].split('|').map(o => o.trim()).filter(Boolean)
})

function handleShareSelect(option) {
  emit('selectOption', option)
}

const occasionOptions = computed(() => {
  if (!props.content) return []
  const match = props.content.match(/\{\{occasion-options:([^}]+)\}\}/)
  if (!match) return []

  return match[1].split(',').map(s => s.trim()).filter(Boolean)
})

// Choice chips ({{intent-options:A,B,C}}) render INLINE where the token sits
// (see messageSegments) — the RM-288 discovery reply interleaves them with
// its lines ("Would you like to see:" / chips / "Or suggestions via OpenAI,
// based on:" / chips), so a trailing block would scramble the reading order.

// Request-expiration chips (Preferred restaurants only), e.g.
// {{expiration-options:In 24 hours,In 48 hours}}
const expirationOptions = computed(() => {
  if (!props.content) return []
  const match = props.content.match(/\{\{expiration-options:([^}]+)\}\}/)
  if (!match) return []

  return match[1].split(',').map(s => s.trim()).filter(Boolean)
})

const memberProfileUserIds = computed(() => {
  if (!props.content) return []
  const matches = props.content.matchAll(/\{\{member-profile-card:([^}]+)\}\}/g)

  return Array.from(matches, m => m[1].trim()).filter(Boolean)
})

/**
 * {{note-choice:restaurantId:entryId|entryId|…}} — the member's notes, listed
 * for "Add this to one of them, or save it as a new note?" (Kapil's copy,
 * 2026-09-23). The server chose them and their order; the numbers are what its
 * "Add to N" chips mean.
 */
const noteChoice = computed(() => {
  if (!props.content) return null
  const match = props.content.match(/\{\{note-choice:([^:}]+):([^}]+)\}\}/)
  if (!match) return null

  return { restaurantId: match[1].trim(), entryIds: match[2].split('|').map(s => s.trim()).filter(Boolean) }
})

// {{send-to-contact:draftId}} — RM-302. The reply carries only this id; the
// destination is fetched over the member's own session by the component.
const sendToContactDraftIds = computed(() => {
  if (!props.content) return []
  const matches = props.content.matchAll(/\{\{send-to-contact:([^}]+)\}\}/g)

  return Array.from(matches, m => m[1].trim()).filter(Boolean)
})

// Markdown-to-HTML for a chunk of assistant text (bold, italic, lists, links): @/utils/agentMessageHtml.
const formatMarkdown = text => formatAgentMarkdown(text, { preferredIconSvg })

// A bare trailing numeral ("1)" / "2.") on the text segment before a row token
// belongs to the row: pull it off and render it on the row's line.
function takeNumeralPrefix(segments) {
  const prev = segments[segments.length - 1]
  if (prev?.kind !== 'text') return null
  const numMatch = prev.raw.match(/(?:^|\n)\s*(\d{1,2}[).])\s*$/)
  if (!numMatch) return null
  const prefix = numMatch[1]
  prev.raw = prev.raw.slice(0, numMatch.index).trim()
  if (prev.raw) {
    prev.html = formatMarkdown(prev.raw)
  } else {
    segments.pop()
  }

  return prefix
}

// Assistant message as ordered segments so restaurant cards render INLINE in
// document order (right where their token sits), not collected into a block.
const messageSegments = computed(() => {
  if (!props.content) return []

  // Strip the non-restaurant tokens — those render in their own blocks below.
  // The model has been seen wrapping the {{small:...}} attribution token in
  // backticks (the prompt used to show it that way), which painted lone
  // "`" lines above and below the attribution (Kapil, prod 2026-09-12).
  // Unwrap the token and drop any line that is only backticks.
  const cleaned = props.content
    .replace(/`+(\{\{small:[^}]+\}\})`+/g, '$1')
    .replace(/^[ \t]*`+[ \t]*$\n?/gm, '')
    .replace(/\{\{occasion-options:[^}]+\}\}\n?/g, '')
    .replace(/\{\{(?:share|note)-options:[^}]+\}\}\n?/g, '')
    .replace(/\{\{note-choice:[^}]+\}\}\n?/g, '')
    .replace(/\{\{expiration-options:[^}]+\}\}\n?/g, '')
    .replace(/\{\{member-profile-card:[^}]+\}\}\n?/g, '')
    .replace(/\{\{send-to-contact:[^}]+\}\}\n?/g, '')
    // An availability or Discovery row still streaming in shows when it is whole, never as raw text.
    .replace(PARTIAL_AVAILABILITY_ROW_RE, '')
    .replace(PARTIAL_DISCOVERY_ROW_RE, '')

  const parts = cleaned.split(/(\{\{spend-options:[^}]+\}\}|\{\{restaurant-(?:card|option):[^}]+\}\}|\{\{intent-options:[^}]+\}\}|\{\{ai-place:[^}]*\}\}|\{\{availability-row:[A-Za-z0-9_-]+\}\}|\{\{discovery-row:[A-Za-z0-9_-]+\}\}|\[[^\]\n]+\]\(\/submit-request\/[a-f0-9]{24}\))/g)
  const segments = []
  let cardIndex = 0
  // A Discovery page (RM-399): its cards say "Favorited by you/your network" over the network's
  // photos (the product agent, 10/20); every other card list keeps "Favorited by". An app-channel
  // page is known by its headers (@/utils/agentMessageHtml); a texted one by its discovery-row tokens.
  const isDiscoveryPage = isDiscoveryPageText(cleaned)
  parts.forEach(part => {
    // {{availability-row:…}} — one restaurant in RM's availability answer (approved 10/2):
    // the AI-chat row, with its times and booking link. A row that doesn't read is not drawn.
    const availabilityMatch = part.match(/^\{\{availability-row:([A-Za-z0-9_-]+)\}\}$/)
    if (availabilityMatch) {
      const row = decodeAvailabilityRow(availabilityMatch[1])
      if (row) segments.push({ kind: 'availability', row, id: row.profileId })

      return
    }
    // {{discovery-row:…}} — one restaurant in a Discovery list done by text or email (Kapil,
    // 10/19): the same card an app-channel Discovery turn shows (the heart filled when the
    // member favorited it, the network's photos), numbered as on its page, with what RM
    // knows under it. The text's own "favorited by …" lines are never printed here: the
    // card carries them. A row that doesn't read is not drawn.
    const discoveryMatch = part.match(/^\{\{discovery-row:([A-Za-z0-9_-]+)\}\}$/)
    if (discoveryMatch) {
      const row = decodeDiscoveryRow(discoveryMatch[1])
      if (!row) return
      if (row.profileId) {
        segments.push({
          kind: 'card',
          id: row.profileId,
          index: cardIndex,
          prefix: row.n ? `${row.n})` : null,
          selectable: false,
          lines: row.facts,
          discovery: true,
        })
        cardIndex += 1
      } else {
        segments.push({ kind: 'text', raw: row.name, html: formatMarkdown(row.name) })
      }

      return
    }
    // RM AI's Review & Submit button (RM-359, 10/27): the Likelihood of Acceptance and Ask RM
    // for feedback first sit right above it, for every member.
    const submitMatch = part.match(/^\[([^\]\n]+)\]\(\/submit-request\/([a-f0-9]{24})\)$/)
    if (submitMatch) {
      segments.push({ kind: 'submit', label: submitMatch[1], id: submitMatch[2], inquiry: /inquiry/i.test(submitMatch[1]) })

      return
    }
    // The spend amounts, inline where their token sits (RM-359, Kapil 10/30): under RM AI's question,
    // its guidance line under them. One row per reply, the first token's (spendOptions reads it).
    if (/^\{\{spend-options:[^}]+\}\}$/.test(part)) {
      if (spendOptions.value.length && !segments.some(seg => seg.kind === 'spend')) segments.push({ kind: 'spend' })

      return
    }
    const chipsMatch = part.match(/^\{\{intent-options:([^}]+)\}\}$/)
    if (chipsMatch) {
      segments.push({
        kind: 'chips',
        options: chipsMatch[1].split(',').map(s => s.trim()).filter(Boolean),
      })

      return
    }
    // {{ai-place:Name|City|Website}} — an OpenAI suggestion that is not on RM:
    // its name with a heart, like a row (RM-288). Takes the list numeral too.
    const placeMatch = part.match(/^\{\{ai-place:([^|}]*)\|?([^|}]*)\|?([^}]*)\}\}$/)
    if (placeMatch) {
      const prefix = takeNumeralPrefix(segments)
      segments.push({
        kind: 'aiPlace',
        name: placeMatch[1].trim(),
        city: placeMatch[2].trim(),
        website: placeMatch[3].trim(),
        prefix,
      })

      return
    }
    const cardMatch = part.match(/^\{\{restaurant-(card|option):([^}]+)\}\}$/)
    if (cardMatch) {
      // AI-suggestion lists number their entries ("1)" / "2." before a row
      // token). Rendered as its own text block the numeral sits orphaned on a
      // line above the card (Kapil, staging 2026-07-17) — so pull a bare
      // trailing numeral off the preceding text segment and render it on the
      // SAME line as the row, matching unresolved entries ("4) I Sodi — …").
      const prefix = takeNumeralPrefix(segments)
      segments.push({
        kind: 'card',
        id: cardMatch[2].trim(),
        index: cardIndex,
        prefix,
        discovery: isDiscoveryPage,
        // A choice row answers the agent's question when tapped, instead of
        // opening the restaurant — so a member disambiguating four identically
        // named locations never has to type a city.
        selectable: cardMatch[1] === 'option',
      })
      cardIndex += 1
    } else if (part.trim()) {
      // The small lines straight after a restaurant row are that row's own (a Favorite's
      // "Booked 10+ times, incl last 3mo"; a Discovery card's facts, 10/22): every one goes in the row.
      const prev = segments[segments.length - 1]
      const { lines, rest } = prev?.kind === 'card' && !prev.selectable ? takeRowLines(part) : { lines: [], rest: part.trim() }
      if (lines.length) prev.lines = [...(prev.lines || []), ...lines]
      if (!rest) return
      // On a Discovery page each category header opens a block of its own, with room above it when
      // it follows a row or the page's first line (the agent-discovery-header class in the template).
      const blocks = isDiscoveryPage ? splitDiscoveryBlocks(rest) : [{ text: rest, header: false }]
      blocks.forEach(block => segments.push({
        kind: 'text', raw: block.text, html: formatMarkdown(block.text), discoveryHeader: block.header && segments.length > 0,
      }))
    }
  })

  return segments
})

// Choice rows show only what disambiguates: "Group · City" under the name.
const choiceMeta = item => {
  const raw = item?.raw ?? item ?? {}
  const group = item?.group ?? raw.group
  const market = item?.market ?? raw.market

  return [group, market].filter(Boolean).join(' · ')
}

// Multi-restaurant replies render compact rows; a single restaurant keeps the
// full card (a confirmation moment earns the space, a list scan doesn't).
// AI-suggestion replies ALWAYS use compact rows — a lone resolved suggestion
// getting the big full card mid-list looks off (Kapil: Daisies, Wicker Park).
const cardSegmentCount = computed(() => messageSegments.value.filter(s => s.kind === 'card').length)
const useCompactCards = computed(() =>
  cardSegmentCount.value > 1
  || /suggestions via OpenAI|AI suggestions \(via OpenAI\)/.test(props.content),
)

// A city-scoped list (the RM-288 discovery flow) repeats the city on every row
// for nothing; a search result spanning cities needs it. Show the market line
// only when this reply's rows come from more than one market.
const rowsSpanMarkets = computed(() => {
  const markets = new Set()
  for (const seg of messageSegments.value) {
    if (seg.kind !== 'card') continue
    const card = restaurantCards.value[seg.id]
    const market = card?.market ?? card?.raw?.market
    if (market) markets.add(market)
  }

  return markets.size > 1
})

const spendExpanded = ref(false)
const SPEND_COLLAPSED_COUNT = 6

const visibleSpendOptions = computed(() => {
  if (spendExpanded.value || spendOptions.value.length <= SPEND_COLLAPSED_COUNT) {
    return spendOptions.value
  }

  return spendOptions.value.slice(0, SPEND_COLLAPSED_COUNT)
})

const hasMoreSpendOptions = computed(() => spendOptions.value.length > SPEND_COLLAPSED_COUNT)

// Answered chip rows stay in the history, grayed out, with the member's tap shown filled — the tap is
// never echoed as a bubble, so hiding the row made the choice vanish (Kapil, 2026-09-16: "Business"
// and "In 24 hours"). Same treatment as the Discover chips.
const isSelectedChip = option => props.selectedOption != null
  && String(option).toLowerCase() === String(props.selectedOption).toLowerCase()
const isSelectedSpend = option => props.selectedOption != null
  && `${option} per person`.toLowerCase() === String(props.selectedOption).toLowerCase()

// A settled spend row still shows the amount the member tapped, even when it sat under "Show higher amounts".
const shownSpendOptions = computed(() => {
  const visible = visibleSpendOptions.value
  if (props.chipsActive) return visible
  const picked = spendOptions.value.find(isSelectedSpend)

  return picked && !visible.includes(picked) ? [...visible, picked] : visible
})

function handleSpendSelect(option) {
  emit('selectOption', `${option} per person`)
}

function handleRestaurantSelect(card) {
  if (!card) return
  const name = card.name ?? card.raw?.name ?? ''
  const market = card.market ?? card.raw?.market ?? ''
  if (!name) return

  // "Maple & Ash in Boston, MA" — the city is what disambiguates, and sending
  // the member's pick as text keeps the agent on one input path whether they
  // tapped a row or typed it themselves.
  emit('selectOption', market ? `${name} in ${market}` : name)
}

function handleOccasionSelect(option) {
  emit('selectOption', option)
}

function handleIntentSelect(option) {
  emit('selectOption', option)
}

// Heart on an OpenAI suggestion that is not on RM: ask the agent to add it
// (Google-linked when Google has it, unlinked otherwise) and favorite it.
function handleAddPlace({ name, city, website }) {
  if (!name) return
  emit('selectOption', `Add ${name}${city ? ` in ${city}` : ''} to my favorites${website ? ` (website: ${website})` : ''}`)
}

function handleExpirationSelect(option) {
  emit('selectOption', option)
}
</script>

<template>
  <!--
    A regenerate re-sends the same member text; show where the redo happened
    instead of repeating the member's bubble. 
  -->
  <div
    v-if="role === 'user' && regenerated"
    class="agent-message__regen-divider d-flex align-center mb-4"
    aria-label="Response was regenerated"
  >
    <VIcon
      icon="mdi-refresh"
      size="12"
      class="mr-1"
    />
    <span>Regenerated response</span>
  </div>
  <div
    v-else
    class="agent-message d-flex mb-4"
    :class="{
      'agent-message--user': role === 'user' && !fromOther,
      'agent-message--other': role === 'user' && fromOther,
      'agent-message--assistant': role !== 'user',
    }"
  >
    <!-- Assistant avatar -->
    <div
      v-if="role !== 'user'"
      class="agent-message__avatar mr-2 flex-shrink-0"
    >
      <VIcon
        icon="mdi-sparkles"
        size="14"
        color="primary-darker"
      />
    </div>

    <div class="agent-message__column">
      <!-- Channel origin (RM-205): only shown for email/SMS-originated messages -->
      <div
        v-if="senderName || channelLabel"
        class="agent-message__channel"
      >
        <span
          v-if="senderName"
          class="agent-message__sender"
        >{{ senderName }}</span>
        <template v-if="senderName && channelLabel">
          ·
        </template>
        {{ channelLabel }}
      </div>

      <!-- Trace icon (admin only, user messages with persisted DB index) -->
      <div
        v-if="showTraceIcon"
        class="agent-message__trace"
      >
        <VBtn
          :icon="hasTrace ? 'mdi-chart-timeline-variant' : 'mdi-chart-timeline-variant-shimmer'"
          size="x-small"
          variant="text"
          :color="hasTrace ? 'primary-dark' : 'medium-emphasis'"
          :title="hasTrace ? 'View agent trace' : 'Trace pending...'"
          :disabled="!hasTrace"
          @click="emit('showTrace', msgDbIndex)"
        />
      </div>

      <!-- Annotate control (admin only, AI replies) -->
      <div
        v-if="showAnnotateIcon"
        class="agent-message__trace"
      >
        <VBtn
          :icon="annotated ? 'mdi-message-alert' : 'mdi-message-plus-outline'"
          size="x-small"
          variant="text"
          :color="annotated ? 'warning' : 'medium-emphasis'"
          :title="annotated ? `Annotated · ${annotationStatusLabel} — edit` : 'Annotate this reply'"
          @click="emit('annotate', msgDbIndex)"
        />
        <!-- Member sentiment on this reply -->
        <VIcon
          v-if="memberRating"
          :icon="memberRating === 'up' ? 'mdi-thumb-up' : 'mdi-thumb-down'"
          size="14"
          :color="memberRating === 'up' ? 'success' : 'warning'"
          :title="`Member rated this reply ${memberRating === 'up' ? 'helpful' : 'not helpful'}`"
          class="ml-1"
        />
      </div>

      <div
        class="agent-message__bubble"
        :class="{
          'agent-message__bubble--user': role === 'user',
          'agent-message__bubble--other': role === 'user' && fromOther,
          'agent-message__bubble--assistant': role === 'assistant',
          'agent-message__bubble--error': role === 'error',
        }"
        :title="bubbleTime || undefined"
      >
        <!-- Admin Text view (RM-399, 10/21): the SMS parts exactly as they went out -->
        <div
          v-if="showSentText"
          class="agent-message__content agent-message__sent"
        >
          <div
            v-for="(part, partIndex) in sentParts"
            :key="partIndex"
            class="agent-message__sent-part"
          >
            <div
              v-if="sentParts.length > 1"
              class="agent-message__sent-caption"
            >
              Text {{ partIndex + 1 }} of {{ sentParts.length }}
            </div>
            <pre class="agent-message__sent-text">{{ part }}</pre>
          </div>
        </div>
        <!-- Assistant: text + restaurant cards rendered in document order -->
        <div
          v-else-if="role === 'assistant'"
          class="agent-message__content"
          @click="handleContentClick"
        >
          <AskRmFeedback
            v-if="askRmId"
            ref="askRmDialog"
            :reservation-id="askRmId"
            :save-first="false"
            hide-button
          />
          <div
            v-if="sentTextMissing"
            class="agent-message__sent-caption"
          >
            Sent text not recorded for this reply (before 10/21) - showing it as written
          </div>
          <template
            v-for="(seg, segIndex) in messageSegments"
            :key="segIndex"
          >
            <!-- eslint-disable vue/no-v-html -->
            <div
              v-if="seg.kind === 'text'"
              :class="{ 'agent-discovery-header': seg.discoveryHeader }"
              v-html="seg.html"
            />
            <!-- eslint-enable vue/no-v-html -->
            <!--
              Choice chips, inline. Live only on the latest reply; on earlier
              turns they stay visible but settled, so the question they answered
              still reads in history.
            -->
            <div
              v-else-if="seg.kind === 'chips'"
              class="agent-intent-options"
              :class="{ 'agent-intent-options--settled': !chipsActive }"
            >
              <div class="agent-intent-options__grid">
                <button
                  v-for="option in seg.options"
                  :key="option"
                  type="button"
                  class="agent-intent-chip"
                  :class="{ 'agent-intent-chip--selected': selectedOption != null && option.toLowerCase() === selectedOption.toLowerCase() }"
                  :disabled="!chipsActive"
                  :aria-pressed="selectedOption != null && option.toLowerCase() === selectedOption.toLowerCase() ? 'true' : undefined"
                  @click="chipsActive && handleIntentSelect(option)"
                >
                  {{ option }}
                </button>
              </div>
            </div>
            <!-- RM-359: the likelihood and Ask RM above RM AI's Review & Submit button (a request, not an inquiry) -->
            <div
              v-else-if="seg.kind === 'submit'"
              class="agent-submit"
            >
              <AgentRequestLikelihood
                v-if="!seg.inquiry && role === 'assistant'"
                :reservation-id="seg.id"
              />
              <p class="agent-submit__cta-line">
                <a
                  :href="`/submit-request/${seg.id}`"
                  class="agent-submit-cta"
                  role="button"
                >{{ seg.label }}</a>
              </p>
            </div>
            <!--
              One restaurant in RM's availability answer (approved 10/2): the shared AI-chat
              row (name, heart + count, "Favorited by"), group and Preferred left off, then the
              times and the booking link. Tapping the row opens the restaurant's RM profile.
            -->
            <div
              v-else-if="seg.kind === 'availability'"
              class="agent-restaurant-cards__item agent-restaurant-cards__item--compact agent-availability"
            >
              <template v-if="seg.row.profileId">
                <AgentRestaurantRow
                  v-if="restaurantCards[seg.row.profileId]"
                  :item="availabilityRowItem(restaurantCards[seg.row.profileId])"
                  hide-note
                >
                  <template #meta>
                    <p
                      v-if="seg.row.times.length"
                      class="agent-availability__times"
                    >
                      {{ seg.row.times.join(', ') }}
                    </p>
                    <p
                      v-for="(line, lineIndex) in seg.row.lines"
                      :key="lineIndex"
                      class="agent-availability__line"
                    >
                      {{ line }}
                    </p>
                    <p
                      v-if="seg.row.url"
                      class="agent-availability__book"
                    >
                      {{ seg.row.appLabel }}
                      <a
                        :href="seg.row.url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="agent-external-link"
                        @click.stop
                      >{{ seg.row.linkLabel }}</a>
                    </p>
                  </template>
                </AgentRestaurantRow>
                <div
                  v-else
                  class="agent-restaurant-cards__loading"
                >
                  <VProgressCircular
                    indeterminate
                    size="18"
                    width="2"
                    color="primary-dark"
                  />
                </div>
              </template>
              <!-- A restaurant not on RM yet: its name with the heart that asks to add it -->
              <template v-else>
                <AgentAiPlaceRow
                  :name="seg.row.name"
                  :active="chipsActive"
                  @add="handleAddPlace"
                />
                <p
                  v-for="(line, lineIndex) in seg.row.lines"
                  :key="lineIndex"
                  class="agent-availability__line"
                >
                  {{ line }}
                </p>
                <p
                  v-if="seg.row.url"
                  class="agent-availability__book"
                >
                  {{ seg.row.appLabel }}
                  <a
                    :href="seg.row.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="agent-external-link"
                  >{{ seg.row.linkLabel }}</a>
                </p>
              </template>
            </div>
            <!--
              The guaranteed spend amounts, where their token sits (RM-359, Kapil 10/30: under RM AI's
              question, its guidance line under them). Settled, like every chip row, once answered.
            -->
            <div
              v-else-if="seg.kind === 'spend'"
              class="agent-spend-options"
              :class="{ 'agent-choice--settled': !chipsActive }"
            >
              <div class="agent-spend-options__label text-caption text-medium-emphasis mb-1">
                Select per person spend
              </div>
              <div class="agent-spend-options__grid">
                <button
                  v-for="option in shownSpendOptions"
                  :key="option"
                  class="agent-spend-chip"
                  :class="{ 'agent-spend-chip--selected': isSelectedSpend(option) }"
                  :disabled="!chipsActive"
                  :aria-pressed="isSelectedSpend(option) ? 'true' : undefined"
                  @click="chipsActive && handleSpendSelect(option)"
                >
                  <span class="agent-spend-chip__amount">{{ option }}</span>
                  <span class="agent-spend-chip__suffix">pp</span>
                </button>
              </div>
              <button
                v-if="hasMoreSpendOptions && chipsActive"
                class="agent-spend-options__toggle text-caption mt-1"
                @click="spendExpanded = !spendExpanded"
              >
                {{ spendExpanded ? 'Show less' : 'Show higher amounts' }}
              </button>
            </div>
            <div
              v-else-if="seg.kind === 'aiPlace'"
              class="agent-restaurant-cards__item agent-restaurant-cards__item--compact"
              :class="{ 'agent-restaurant-cards__item--numbered': !!seg.prefix }"
            >
              <span
                v-if="seg.prefix"
                class="agent-restaurant-cards__num"
              >{{ seg.prefix }}</span>
              <AgentAiPlaceRow
                :name="seg.name"
                :city="seg.city"
                :website="seg.website"
                :active="chipsActive || true"
                @add="handleAddPlace"
              />
            </div>
            <div
              v-else
              class="agent-restaurant-cards__item"
              :class="{ 'agent-restaurant-cards__item--compact': useCompactCards, 'agent-restaurant-cards__item--numbered': !!seg.prefix }"
            >
              <span
                v-if="seg.prefix"
                class="agent-restaurant-cards__num"
              >{{ seg.prefix }}</span>
              <template v-if="restaurantCards[seg.id]">
                <!--
                  A choice row stays a choice row after the member has answered: same plain
                  shape, just no longer tappable. It used to fall through to the full browse
                  card (photo, counts, "+") the moment a newer message arrived (Kapil, 2026-09-11).
                -->
                <div
                  v-if="seg.selectable"
                  class="agent-restaurant-choice"
                  :class="{ 'agent-restaurant-choice--settled': !chipsActive }"
                  :role="chipsActive ? 'button' : undefined"
                  :tabindex="chipsActive ? 0 : undefined"
                  :aria-disabled="chipsActive ? undefined : 'true'"
                  :aria-label="`Choose ${restaurantCards[seg.id].name ?? ''}${restaurantCards[seg.id].market ? ', ' + restaurantCards[seg.id].market : ''}`"
                  @click.capture.stop="chipsActive && handleRestaurantSelect(restaurantCards[seg.id])"
                  @keydown.enter.prevent="chipsActive && handleRestaurantSelect(restaurantCards[seg.id])"
                  @keydown.space.prevent="chipsActive && handleRestaurantSelect(restaurantCards[seg.id])"
                >
                  <!--
                    A choice is an answer, not a browse: name, group, city. No photo, no
                    counts, no favorite control competing with the tap (Kapil, 2026-09-11).
                  -->
                  <span class="agent-restaurant-choice__name">{{ restaurantCards[seg.id].name ?? restaurantCards[seg.id].raw?.name }}</span>
                  <span
                    v-if="choiceMeta(restaurantCards[seg.id])"
                    class="agent-restaurant-choice__meta"
                  >{{ choiceMeta(restaurantCards[seg.id]) }}</span>
                </div>
                <AgentRestaurantRow
                  v-else-if="useCompactCards"
                  :item="restaurantCards[seg.id]"
                  :show-market="rowsSpanMarkets"
                  :favorited-by-label="seg.discovery ? 'Favorited by you/your network' : 'Favorited by'"
                >
                  <!-- The row's own lines (a Favorite's booking line, a Discovery card's facts): under the restaurant's facts -->
                  <template
                    v-if="seg.lines?.length"
                    #signals
                  >
                    <p
                      v-for="(line, lineIndex) in seg.lines"
                      :key="lineIndex"
                      class="agent-restaurant-cards__line"
                    >
                      {{ line }}
                    </p>
                  </template>
                </AgentRestaurantRow>
                <RestaurantGridCard
                  v-else
                  :item="restaurantCards[seg.id]"
                  :index="seg.index"
                  :friends-who-favorited-prop="restaurantCards[seg.id].friendsWhoFavorited || null"
                  :favorited-by-label="seg.discovery ? 'Favorited by you/your network' : 'Favorited by'"
                />
                <p
                  v-for="(line, lineIndex) in (!useCompactCards && seg.lines) || []"
                  :key="lineIndex"
                  class="agent-restaurant-cards__line"
                >
                  {{ line }}
                </p>
              </template>
              <div
                v-else
                class="agent-restaurant-cards__loading"
              >
                <VProgressCircular
                  indeterminate
                  size="18"
                  width="2"
                  color="primary-dark"
                />
              </div>
            </div>
          </template>
        </div>
        <div
          v-else
          class="agent-message__content"
        >
          <a
            v-if="attachedImageId && attachedFileType === 'pdf'"
            :href="mediaUrl(attachedImageId, 'original')"
            target="_blank"
            rel="noopener"
            class="agent-message__attached-pdf"
            @click.stop
          >
            <VIcon
              size="16"
              icon="mdi-file-pdf-box"
            />
            <span>PDF attached</span>
          </a>
          <img
            v-else-if="attachedImageId"
            :src="mediaUrl(attachedImageId, 'regular')"
            class="agent-message__attached-image"
            alt="Attached image — tap to view full size"
            @click="zoomOpen = true"
          >
          <template v-if="content">
            {{ content }}
          </template>
        </div>
      </div>

      <!-- "Add this to one of them…": the member's notes, each tappable in full -->
      <AgentNoteChoice
        v-if="noteChoice"
        :restaurant-id="noteChoice.restaurantId"
        :entry-ids="noteChoice.entryIds"
      />

      <!-- RM-302: the drafted message, with Text / Email the member taps -->
      <AgentSendToContact
        v-for="draftId in sendToContactDraftIds"
        :key="`send-${draftId}`"
        :draft-id="draftId"
      />

      <!-- Member profile cards (inline) -->
      <div
        v-for="userId in memberProfileUserIds"
        :key="`profile-${userId}`"
        class="agent-member-profile mt-2"
      >
        <MemberProfileCard
          v-if="memberProfiles[userId]"
          :member="memberProfiles[userId]"
        />
        <div
          v-else
          class="agent-member-profile__loading d-flex align-center justify-center pa-4"
        >
          <VProgressCircular
            indeterminate
            size="20"
            width="2"
            color="primary-dark"
          />
        </div>
      </div>

      <!-- Occasion option chips -->
      <div
        v-if="shareOptions.length > 0"
        class="agent-occasion-options mt-2"
        :class="{ 'agent-choice--settled': !chipsActive }"
      >
        <div class="agent-occasion-options__grid">
          <button
            v-for="option in shareOptions"
            :key="option"
            class="agent-occasion-chip"
            :class="{ 'agent-occasion-chip--selected': isSelectedChip(option) }"
            :disabled="!chipsActive"
            :aria-pressed="isSelectedChip(option) ? 'true' : undefined"
            @click="chipsActive && handleShareSelect(option)"
          >
            {{ option }}
          </button>
        </div>
      </div>
      <div
        v-if="occasionOptions.length > 0"
        class="agent-occasion-options mt-2"
        :class="{ 'agent-choice--settled': !chipsActive }"
      >
        <div class="agent-occasion-options__label text-caption text-medium-emphasis mb-1">
          Select occasion
        </div>
        <div class="agent-occasion-options__grid">
          <button
            v-for="option in occasionOptions"
            :key="option"
            class="agent-occasion-chip"
            :class="{ 'agent-occasion-chip--selected': isSelectedChip(option) }"
            :disabled="!chipsActive"
            :aria-pressed="isSelectedChip(option) ? 'true' : undefined"
            @click="chipsActive && handleOccasionSelect(option)"
          >
            {{ option }}
          </button>
        </div>
      </div>

      <!-- Request-expiration chips (Preferred restaurants only) -->
      <div
        v-if="expirationOptions.length > 0"
        class="agent-occasion-options mt-2"
        :class="{ 'agent-choice--settled': !chipsActive }"
      >
        <div class="agent-occasion-options__label text-caption text-medium-emphasis mb-1">
          Request expires in
        </div>
        <div class="agent-occasion-options__grid">
          <button
            v-for="option in expirationOptions"
            :key="option"
            class="agent-occasion-chip"
            :class="{ 'agent-occasion-chip--selected': isSelectedChip(option) }"
            :disabled="!chipsActive"
            :aria-pressed="isSelectedChip(option) ? 'true' : undefined"
            @click="chipsActive && handleExpirationSelect(option)"
          >
            {{ option }}
          </button>
        </div>
      </div>

      <!-- Member stopped this reply mid-generation -->
      <div
        v-if="role === 'assistant' && stopped"
        class="agent-message__stopped-caption"
      >
        <VIcon
          icon="mdi-stop-circle-outline"
          size="12"
          class="mr-1"
        />
        <span>Stopped</span>
      </div>

      <!-- Turn latency caption (admin viewer only) -->
      <div
        v-if="turnTraceCaption"
        class="agent-message__trace-caption"
      >
        {{ turnTraceCaption }}
      </div>

      <!--
        Timestamp for messages WITHOUT an action bar (member messages, and any
        assistant reply that has no actions). Where the action bar exists the
        time folds into it, so it no longer floats on its own line above.
      -->
      <div
        v-if="bubbleTime && !showActionBar"
        class="agent-message__time-inline"
      >
        {{ bubbleTime }}
      </div>

      <!--
        Action bar: one row, one baseline. Icons and the timestamp share the
        left group; "Questions?" is a ghost pill on the right — the only
        element with a container, so it reads as the action rather than a
        footnote among glyphs. The timestamp used to sit on its own line above
        this row, which on mobile pushed the row wide enough to need a swipe.
      -->
      <div
        v-if="showActionBar"
        class="agent-message__actions"
      >
        <div class="agent-message__actions-group">
          <template v-if="showMemberActions">
            <VBtn
              v-tooltip:top="effectiveRating === 'up' ? 'Remove' : 'Helpful'"
              :icon="effectiveRating === 'up' ? 'mdi-thumb-up' : 'mdi-thumb-up-outline'"
              variant="text"
              class="agent-message__action-btn"
              :class="{ 'agent-message__action-btn--on': effectiveRating === 'up' }"
              @click="rateReply('up')"
            />
            <VBtn
              v-tooltip:top="effectiveRating === 'down' ? 'Remove' : 'Not helpful'"
              :icon="effectiveRating === 'down' ? 'mdi-thumb-down' : 'mdi-thumb-down-outline'"
              variant="text"
              class="agent-message__action-btn"
              :class="{ 'agent-message__action-btn--on': effectiveRating === 'down' }"
              @click="rateReply('down')"
            />
            <VBtn
              v-tooltip:top="copied ? 'Copied' : 'Copy text'"
              :icon="copied ? 'mdi-checkbox-multiple-marked' : 'mdi-content-copy'"
              variant="text"
              class="agent-message__action-btn"
              :class="{ 'agent-message__action-btn--on': copied }"
              @click="copyReplyText"
            />
          </template>
          <VBtn
            v-if="showRegenerate"
            v-tooltip:top="'Regenerate response'"
            icon="mdi-refresh"
            variant="text"
            class="agent-message__action-btn"
            @click="emit('regenerate')"
          />

          <span
            v-if="bubbleTime"
            class="agent-message__actions-divider"
            aria-hidden="true"
          />
          <span
            v-if="bubbleTime"
            class="agent-message__actions-time"
          >{{ bubbleTime }}</span>
        </div>

        <VBtn
          v-if="showMemberActions"
          variant="text"
          class="agent-message__help-pill"
          prepend-icon="mdi-chat-outline"
          @click="emit('openHelp')"
        >
          Questions?
        </VBtn>
      </div>

      <!-- Mobile: the time gets its own line so the row never needs a swipe. -->
      <div
        v-if="showActionBar && bubbleTime"
        class="agent-message__actions-time-below"
      >
        {{ bubbleTime }}
      </div>
    </div>

    <!-- Per-message timestamp revealed by swiping the message list left -->
    <div
      v-if="bubbleTime"
      class="agent-message__time"
      aria-hidden="true"
    >
      {{ bubbleTime }}
    </div>

    <!--
      The attached image, full size, without leaving the app — the same pattern
      as the saved card on My Notes. `original` rather than a rendition: this is
      the view a member zooms into to read a phone number off a card.
    -->
    <VDialog
      v-if="attachedImageId"
      v-model="zoomOpen"
      max-width="720"
    >
      <VCard class="agent-message__zoom-card">
        <img
          :src="mediaUrl(attachedImageId, 'original')"
          alt="Attached image, full size"
        >
        <div class="agent-message__zoom-actions">
          <VBtn
            variant="text"
            size="small"
            @click="zoomOpen = false"
          >
            Close
          </VBtn>
        </div>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped>
.agent-message {
  align-items: flex-start;
  position: relative;
  /* Row spans the full message-container width so its right edge aligns with
     the container's clip edge; the 720px reading cap lives in this padding,
     matching the previous centered layout on wider screens. */
  padding-left: max(0px, calc(50% - 360px));
  padding-right: max(0px, calc(50% - 360px));
}

/* Channel origin label (RM-205): metadata tier — smallest size, tertiary color,
   no container. Right-aligned on the member's own (right-side) messages. */
.agent-message__channel {
  font-size: 11px;
  line-height: 1.2;
  color: var(--ds-text-tertiary, rgba(0, 0, 0, 0.45));
  margin-bottom: 2px;
}

.agent-message--user .agent-message__channel {
  text-align: right;
}

/* Who wrote it, in a group text thread: the one line a reader scans for. */
.agent-message__sender {
  font-size: 12px;
  font-weight: 500;
  color: var(--ds-text-strong, rgba(0, 0, 0, 0.87));
}

/* Per-message timestamp: lives just past the right edge of the row, revealed
   when the parent drag layer translates left (iMessage-style). The offset is
   chosen so that the timestamp's left edge sits past the messages container's
   right padding at rest (cleanly clipped by overflow-x: hidden). */
.agent-message__trace-caption {
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), 0.45);
  margin-top: 2px;
  padding-left: 4px;
}

/* ── Action bar ───────────────────────────────────────────────────────────
   One row, one baseline: icon group + timestamp on the left, "Questions?" is the
   only contained element on the right.

   Colours come from the palette (--ds-*), never the design spec's literal
   hexes: every shade it named already has a token by ROLE — muted icon,
   inset hover, light border, disabled-tone timestamp, brand tint for the
   pill — and raw hex here would drift from the rest of the app the first
   time the palette moves.
*/
.agent-message__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 6px;
}

.agent-message__actions-group {
  display: flex;
  align-items: center;
  min-width: 0;
}

/* The icons show at full strength on every reply — a hover-reveal fade made
   them read as disabled controls. State is carried by the icon itself:
   outline = untouched, filled (--on) = the member rated or copied. */
.agent-message__action-btn {
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 8px;
  color: var(--ds-text-muted);
}

.agent-message__action-btn:hover {
  background-color: var(--ds-surface-inset);
  color: var(--ds-text-strong);
}

/* An applied rating (or a just-copied reply) stays legible without the pill's
   weight. */
.agent-message__action-btn--on {
  color: var(--ds-text-strong);
}

.agent-message__actions-divider {
  flex: 0 0 auto;
  width: 1px;
  height: 18px;
  margin: 0 10px 0 6px;
  background-color: var(--ds-border-light);
}

.agent-message__actions-time {
  font-size: 11.5px;
  line-height: 1;
  color: var(--ds-text-strong); /* always legible — a disabled tone read as "hard to see" */
  white-space: nowrap;
  user-select: none;
}

/* Only shown on narrow viewports — see the media query below. */
.agent-message__actions-time-below {
  display: none;
  margin-top: 4px;
  padding-left: 4px;
  font-size: 11px;
  line-height: 1;
  color: var(--ds-text-strong);
  user-select: none;
}

.agent-message__help-pill {
  flex: 0 0 auto;
  height: 32px;
  min-width: 0;
  padding-inline: 14px;
  border: 1px solid var(--ds-brand-primary);
  border-radius: 16px;
  background-color: var(--ds-brand-primary-light);
  color: var(--ds-brand-primary-dark);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
}

.agent-message__help-pill:hover {
  background-color: var(--ds-brand-primary-light);
  border-color: var(--ds-brand-primary-dark);
}

/* Below 768px the timestamp leaves the row entirely: with it inline the row
   could not fit a 320px viewport and members had to swipe to read the time. */
@media (max-width: 767px) {
  .agent-message__actions-group {
    gap: 0;
    margin-left: -8px;
  }

  .agent-message__action-btn {
    width: 36px;
    height: 36px;
    min-width: 36px;
  }

  .agent-message__actions-divider,
  .agent-message__actions-time {
    display: none;
  }

  .agent-message__actions-time-below {
    display: block;
  }

  .agent-message__help-pill {
    height: 36px;
    padding-inline: 12px;
    font-size: 12.5px;
  }
}

.agent-message__stopped-caption {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), 0.45);
  margin-top: 2px;
  padding-left: 4px;
}
.agent-message__regen-divider {
  justify-content: center;
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), 0.45);
}

/* Timestamps: always visible on pointer devices (transparency), swipe-to-reveal
   stays the mechanism on touch where the inline line would crowd the thread. */
.agent-message__time-inline {
  display: none;
  font-size: 11px;
  color: var(--ds-text-tertiary, rgba(0, 0, 0, 0.45));
  margin-top: 2px;
  padding-left: 4px;
  user-select: none;
}

@media (hover: hover) and (pointer: fine) {
  .agent-message__time-inline {
    display: block;
  }
}

.agent-message--user .agent-message__time-inline {
  text-align: right;
  padding-right: 4px;
}

.agent-message__time {
  position: absolute;
  top: 14px;
  right: -76px;
  width: 56px;
  font-size: 11px;
  line-height: 1.2;
  color: var(--ds-text-tertiary, rgba(0, 0, 0, 0.45));
  white-space: nowrap;
  text-align: left;
  pointer-events: none;
  user-select: none;
}

.agent-message--user {
  justify-content: flex-end;
}

.agent-message--assistant,
.agent-message--other {
  justify-content: flex-start;
}

/* Subtle spark marker for RM AI replies — no filled circle (cleaner than a
   framed avatar). */
.agent-message__avatar {
  width: 22px;
  height: 22px;
  min-width: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 3px;
}

.agent-message__column {
  max-width: 82%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: inherit;
}

/* RM AI replies render as plain text (no card), so let them use the full
   reading width. */
.agent-message--assistant .agent-message__column {
  max-width: 100%;
}

.agent-message--user .agent-message__column {
  align-items: flex-end;
}

.agent-message__trace {
  margin-bottom: -4px;
}

.agent-message__bubble {
  word-wrap: break-word;
  overflow-wrap: anywhere;
  min-width: 0;
  /* Cap to the column so restaurant cards shrink to fit instead of
     overflowing the viewport on mobile (their natural width is wider). */
  max-width: 100%;
  line-height: 1.6;
}

/* Member message: soft warm-neutral pill, fully rounded (no notch). */
.agent-message__bubble--user {
  padding: 10px 16px;
  background: #eceae6;
  color: #1a1a1a;
  border-radius: 18px;
}

/* Someone else on a group text thread (RM-398): the same pill, on the left,
   outlined instead of filled, so the viewer's own messages are the only
   filled ones. */
.agent-message__bubble--other {
  background: transparent;
  border: 1px solid var(--ds-border-light, rgba(0, 0, 0, 0.12));
}

/* RM AI reply: no card — plain text on the background, roomier. */
.agent-message__bubble--assistant {
  padding: 2px 0;
  background: transparent;
  color: #1a1a1a;
}

.agent-message__bubble--error {
  padding: 10px 14px;
  background: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
  border-radius: 16px;
}

.agent-message__content {
  font-size: 16px;
  line-height: 1.6;
  max-width: 100%;
}

/* Image the member attached (profile photo flow) — thumbnail inside the pill. */
/*
  A card is wide, and 160px of it cropped square showed a corner. Bigger box,
  aspect preserved, never cropped — and the `regular` (1080px) rendition rather
  than `small` (400px), so it stays sharp on a retina screen. Tap to open it
  full size (Kapil, 2026-09-23).
*/
.agent-message__attached-image {
  display: block;
  max-width: min(260px, 72%);
  max-height: 220px;
  width: auto;
  border-radius: 12px;
  margin: 2px 0 6px;
  object-fit: contain;
  cursor: zoom-in;
}

.agent-message__zoom-card img {
  display: block;
  width: 100%;
  height: auto;
}

.agent-message__zoom-actions {
  display: flex;
  justify-content: flex-end;
  padding: 4px 8px 8px;
}

.agent-message__attached-pdf {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 2px 0 6px;
  padding: 6px 10px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.06);
  color: inherit;
  font-size: 13px;
  text-decoration: none;
}

.agent-message__content :deep(ul) {
  padding-left: 1.25rem;
  margin: 0.25rem 0;
}

.agent-message__content :deep(li) {
  margin: 0.125rem 0;
}

.agent-message__content :deep(strong) {
  font-weight: 600;
}

/* {{small:...}} token — tertiary metadata line (e.g. AI-suggestion attribution).
   Block-level so it reads as a caption above the content it annotates. */
.agent-message__content :deep(.agent-small) {
  display: block;
  font-size: 12px;
  line-height: 1.5;
  color: var(--ds-text-tertiary, rgba(0, 0, 0, 0.45));
  margin-bottom: 4px;
}

.agent-message__content :deep(a) {
  color: var(--ds-brand-primary-darker);
  text-decoration: underline;
}

.agent-message__bubble--user .agent-message__content :deep(a) {
  color: var(--ds-brand-primary-darker);
  text-decoration: underline;
}

.agent-message__content :deep(a.agent-restaurant-link) {
  font-weight: 500;
  text-decoration: none;
}

.agent-message__content :deep(a.agent-restaurant-link:hover) {
  text-decoration: underline;
}

.agent-message__content :deep(a.agent-info-link) {
  font-weight: 500;
  text-decoration: none;
}

.agent-message__content :deep(a.agent-info-link:hover) {
  text-decoration: underline;
}

.agent-message__content :deep(a.agent-internal-link) {
  font-weight: 500;
  text-decoration: none;
}

.agent-message__content :deep(a.agent-internal-link:hover) {
  text-decoration: underline;
}

.agent-message__content :deep(a.agent-external-link) {
  font-weight: 500;
  text-decoration: none;
}

.agent-message__content :deep(a.agent-external-link:hover) {
  text-decoration: underline;
}

/* Review & Submit Request — primary hand-off CTA (dominant action) */
.agent-message__content :deep(a.agent-submit-cta) {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 48px;
  margin: 12px 0 4px;
  padding: 12px 20px;
  border-radius: 12px;
  background: var(--ds-brand-primary-dark);
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  box-shadow: var(--ds-shadow-xs);
  transition: background 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.agent-message__content :deep(a.agent-submit-cta)::after {
  content: '→';
  font-size: 18px;
  line-height: 1;
}

.agent-message__content :deep(a.agent-submit-cta:hover) {
  background: var(--ds-brand-primary-darker);
  box-shadow: var(--ds-shadow-sm);
  text-decoration: none;
}

.agent-message__content :deep(a.agent-submit-cta:active) {
  transform: scale(0.985);
}

.agent-message__content :deep(a.agent-submit-cta:focus-visible) {
  outline: 2px solid var(--ds-brand-primary-darker);
  outline-offset: 2px;
}

/* Spend option chips */
.agent-spend-options {
  max-width: 100%;
  margin-block-start: 8px;
}

/* RM AI's guidance line under the amounts (RM-359, 10/30): a paragraph's space before it */
.agent-spend-options:not(:last-child) {
  margin-block-end: 16px;
}

.agent-spend-options__label {
  padding-left: 2px;
  font-size: 11px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  font-weight: 600;
}

.agent-spend-options__grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.agent-spend-chip {
  display: inline-flex;
  align-items: baseline;
  gap: 2px;
  padding: 6px 12px;
  border-radius: 20px;
  border: 1px solid var(--ds-border-light);
  background: var(--ds-surface-card);
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  line-height: 1;
}

.agent-spend-chip:hover {
  border-color: var(--ds-brand-primary-dark);
  background: var(--ds-brand-primary-light);
  box-shadow: var(--ds-shadow-xs);
}

.agent-spend-chip:active {
  transform: scale(0.96);
}

.agent-spend-chip__amount {
  font-size: 14px;
  font-weight: 600;
  color: var(--ds-text-primary);
}

.agent-spend-chip__suffix {
  font-size: 11px;
  font-weight: 500;
  color: var(--ds-text-secondary);
}

.agent-spend-options__toggle {
  background: none;
  border: none;
  padding: 2px 4px;
  color: var(--ds-brand-primary-darker);
  font-weight: 600;
  cursor: pointer;
}

.agent-spend-options__toggle:hover {
  text-decoration: underline;
}

/* Occasion option chips */
.agent-member-profile {
  max-width: 100%;
}

.agent-member-profile__loading {
  background: #f8f8f9;
  border-radius: 12px;
  min-height: 120px;
}

.agent-restaurant-cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 100%;
}

.agent-restaurant-cards__item {
  width: 100%;
  margin-block: 8px;
}

/* A row's own line ("Booked 10+ times, incl last 3mo"): the Favorites page's booking line, same scale and ink */
.agent-restaurant-cards__line {
  margin: 2px 0 0;
  font-family: var(--ds-font-body);
  font-size: 13.5px;
  font-weight: var(--ds-font-medium, 500);
  line-height: 1.3;
  color: var(--ds-brand-ink, inherit);
}

/* A Discovery category header after a row or the page's first line: a block with room above it,
   so "Trending on RM" never sits on the card before it (Kapil, 10/22). Rows keep their 8px
   between each other; a row's 8px and this collapse to the 16px. */
.agent-discovery-header {
  margin-top: 16px;
}

/*
  Availability rows (approved 10/2): the times are the answer, so they read at the
  chat's body size in strong ink; the result's own sentence and the booking line sit
  one step down (14px), the provider's name the only link. 4px steps, no new boxes.
*/
.agent-availability__times {
  margin: 4px 0 0;
  font-family: var(--ds-font-body);
  font-size: 16px;
  line-height: 1.4;
  color: var(--ds-text-strong);
}

.agent-availability__line {
  margin: 4px 0 0;
  font-family: var(--ds-font-body);
  font-size: 14px;
  line-height: 1.4;
  color: var(--ds-text-body);
  white-space: normal;
  word-break: break-word;
}

.agent-availability__book {
  margin: 4px 0 0;
  font-family: var(--ds-font-body);
  font-size: 14px;
  line-height: 1.4;
  color: var(--ds-text-body);
}

/* A restaurant not on RM: its lines under the name, the row's own left edge */
.agent-availability > .agent-availability__line,
.agent-availability > .agent-availability__book {
  padding-inline: 2px;
}

/* Numbered AI-suggestion entry: the list numeral sits on the SAME line as the
   restaurant row (matching unresolved "4) I Sodi — …" entries) instead of
   orphaned on its own line above the card. */
.agent-restaurant-cards__item--numbered {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.agent-restaurant-cards__item--numbered > :not(.agent-restaurant-cards__num) {
  flex: 1;
  min-width: 0;
}

/* Sits on the name line (row padding 8px + the 17px name's line box), so the
   numeral reads with the restaurant name and the rest of the row hangs below
   it — not centered against the group line (Kapil, 2026-09-15) */
.agent-restaurant-cards__num {
  flex: 0 0 auto;
  margin-top: 10px;
  font-size: 16px;
  line-height: 1;
  color: var(--ds-text-primary, inherit);
}

.agent-restaurant-cards__loading {
  min-height: 92px;
  border-radius: 12px;
  border: 1px solid var(--ds-border-light);
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.agent-occasion-options {
  max-width: 100%;
}

.agent-occasion-options__label {
  padding-left: 2px;
  font-size: 11px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  font-weight: 600;
}

.agent-occasion-options__grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.agent-occasion-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid var(--ds-border-light);
  background: var(--ds-surface-card);
  font-size: 14px;
  font-weight: 500;
  color: var(--ds-text-primary);
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  line-height: 1;
}

.agent-occasion-chip:hover {
  border-color: var(--ds-brand-primary-dark);
  background: var(--ds-brand-primary-light);
  box-shadow: var(--ds-shadow-xs);
}

.agent-occasion-chip:active {
  transform: scale(0.96);
}

/* Answered spend / occasion / expiration rows: legible in history, no longer inviting; the tapped
   chip is filled (Kapil, 2026-09-16) — same look as the settled Discover chips */
.agent-choice--settled .agent-occasion-chip,
.agent-choice--settled .agent-spend-chip {
  cursor: default;
  border-color: var(--ds-border-strong);
  color: var(--ds-text-tertiary, rgba(0, 0, 0, 0.45));
  background: transparent;
  box-shadow: none;
  transform: none;
}

.agent-choice--settled .agent-spend-chip__amount,
.agent-choice--settled .agent-spend-chip__suffix {
  color: var(--ds-text-tertiary, rgba(0, 0, 0, 0.45));
}

.agent-occasion-chip--selected,
.agent-occasion-chip--selected:disabled,
.agent-choice--settled .agent-occasion-chip--selected,
.agent-spend-chip--selected,
.agent-spend-chip--selected:disabled,
.agent-choice--settled .agent-spend-chip--selected {
  background: var(--ds-brand-primary-dark);
  border-color: var(--ds-brand-primary-dark);
  color: #fff;
  opacity: 1;
}

.agent-spend-chip--selected .agent-spend-chip__amount,
.agent-spend-chip--selected .agent-spend-chip__suffix,
.agent-choice--settled .agent-spend-chip--selected .agent-spend-chip__amount,
.agent-choice--settled .agent-spend-chip--selected .agent-spend-chip__suffix {
  color: #fff;
}

/* A row offered as an answer. The affordance is the whole row — one target,
   no competing controls — with the same quiet hover the chips use. This block
   is plain CSS (the file's <style> is not scss): flat selectors, no nesting. */
.agent-restaurant-choice {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-height: 48px;
  padding: 10px 14px;
  cursor: pointer;
  border: 1px solid var(--ds-border-light, rgba(26, 26, 26, 0.12));
  border-radius: 12px;
  background: var(--ds-surface-card, #fff);
  transition: background-color 150ms ease, border-color 150ms ease;
}

.agent-restaurant-choice__name {
  font-size: 16px;
  font-weight: 500;
  line-height: 1.3;
  color: var(--ds-text-primary, #1a1a1a);
}

.agent-restaurant-choice__meta {
  font-size: 14px;
  line-height: 1.3;
  color: var(--ds-text-secondary, rgba(26, 26, 26, 0.62));
}

.agent-restaurant-choice:hover {
  border-color: var(--ds-brand-primary);
  background-color: rgb(var(--v-theme-primary), 0.04);
}

/* Answered: the rows stay for context, quieter, and stop reacting. */
.agent-restaurant-choice--settled {
  cursor: default;
  opacity: 0.6;
}

.agent-restaurant-choice--settled:hover {
  border-color: var(--ds-border-light, rgba(26, 26, 26, 0.12));
  background-color: var(--ds-surface-card, #fff);
}

.agent-restaurant-choice:focus-visible {
  outline: 2px solid var(--ds-brand-primary-dark);
  outline-offset: 2px;
}

.agent-intent-options {
  max-width: 100%;
}

.agent-intent-options__grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.agent-intent-chip {
  display: inline-flex;
  align-items: center;
  padding: 8px 18px;
  border-radius: 20px;
  border: 1px solid var(--ds-brand-primary-dark);
  background: var(--ds-surface-card);
  font-size: 15px;
  font-weight: 600;
  color: var(--ds-brand-primary-dark);
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  line-height: 1;
}

.agent-intent-chip:hover {
  background: var(--ds-brand-primary-light);
  box-shadow: var(--ds-shadow-xs);
}

.agent-intent-chip:active {
  transform: scale(0.96);
}

/* Inline chips sit between the reply's lines; a small gap keeps them read as
   part of the sentence above them. */
.agent-message__content .agent-intent-options {
  margin: 6px 0 10px;
}

/* Settled: the question stays legible in history, the chips no longer invite */
.agent-intent-options--settled .agent-intent-chip {
  cursor: default;
  border-color: var(--ds-border-strong);
  color: var(--ds-text-tertiary, rgba(0, 0, 0, 0.45));
  background: transparent;
}

.agent-intent-options--settled .agent-intent-chip:hover {
  background: transparent;
  box-shadow: none;
}

/* The chip the member tapped: filled, so the choice reads in place of an
   echoed member bubble (Kapil, 2026-09-15) */
.agent-intent-options .agent-intent-chip--selected,
.agent-intent-options--settled .agent-intent-chip--selected,
.agent-intent-options--settled .agent-intent-chip--selected:disabled {
  background: var(--ds-brand-primary-dark);
  border-color: var(--ds-brand-primary-dark);
  color: #fff;
  opacity: 1;
  transition: none;
}

/* "Add to your favorites" on an OpenAI suggestion that is not on RM — reads
   like the reply's other links, sized for a thumb */
.agent-message__content :deep(.agent-add-favorite) {
  display: inline;
  padding: 4px 0;
  border: 0;
  background: none;
  font: inherit;
  color: var(--ds-brand-primary-darker);
  text-decoration: underline;
  cursor: pointer;
}

/* RM-399 (10/21): the admin viewer's Text view - each text part as it went out */
.agent-message__sent-part + .agent-message__sent-part {
  margin-top: 12px;
}
.agent-message__sent-caption {
  font-size: 12px;
  opacity: 0.7;
  margin-bottom: 4px;
}
.agent-message__sent-text {
  margin: 0;
  font-family: inherit;
  font-size: inherit;
  white-space: pre-wrap;
  word-break: break-word;
}

.agent-submit__cta-line {
  margin: 0;
}
</style>
